import type { AppDataStore, IDAgent, IDManagedAgent } from './agent/index.js';
import * as Sdk from '@dwn-protocol/id';
import ms from 'ms';
import { IDUserAgent } from './user-agent/index.js';

import { DwnApi } from './dwn-api.js';
import { DidApi } from './did-api.js';
import { getServiceDwnEndpoints } from './service-options.js';
import { DidKeyMethod, DidDhtMethod, DidIonMethod, DidDht } from './dids/index.js';
import { Metadata } from './interfaces/metadata.js';
import { Queue } from './interfaces/queue.js';
import { Services } from './interfaces/services.js';
import { Transactions } from './interfaces/transactions.js';
import { Jose } from './crypto/index.js';
import { VcApi } from './vc-api.js';
import { Jws } from '@dwn-protocol/id';

/**
 * Override defaults.
 */
export type ServiceOptions = {
  // Override default dwnEndpoints provided.
  dwnEndpoints?: string[];
  // Override the did:dht pkarr relay endpoint used by SDK resolution/publish.
  dhtRelayUrl?: string;
}

/**
 * Optional overrides that can be provided when calling {@link IDDwn.connect}.
 */
export type IDConnectOptions = {
  /** Provide a {@link IDAgent} implementation. Defaults to creating a local
   * {@link IDUserAgent} if one isn't provided */
  agent?: IDAgent;

  /** Provide an instance of a {@link AppDataStore} implementation. Defaults to
   * a LevelDB-backed store with an insecure, static unlock passphrase if one
   * isn't provided. To allow the app user to enter a secure passphrase of
   * their choosing, provide an initialized {@link AppDataStore} instance. */
  appData?: AppDataStore;

  // Specify an existing DID to connect to.
  connectedDid?: string;

  /** Specify the DID method to use when creating a new identity.
   * Defaults to 'ion'. Supported methods: 'ion', 'dht', 'key' */
  didMethod?: 'ion' | 'dht' | 'key';

  /** Enable synchronization of DWN records between local and remote DWNs.
   * Sync defaults to running every 30 seconds and can be set to any value accepted by `ms()`.
   * To disable sync set to 'off'. */
  sync?: string;

  /** When true (default), failed outbound DWN sends are queued in an outbox and replayed when online.
   * Set to false to throw on send failure instead of queuing. */
  queueWhenOffline?: boolean;

  /** When true and in a browser, run sync and outbox drain immediately when the window 'online' event fires.
   * Default false. In Node, use flushOutboxAndSync() after your own connectivity check. */
  flushWhenOnline?: boolean;

  /** Override defaults service options.
   * See {@link ServiceOptions} for available options. */
  serviceOptions?: ServiceOptions;

  passphrase?: string;
}

/**
 * @see {@link IDConnectOptions}
 */
type IDOptions = {
  agent: IDAgent;
  connectedDid: string;
};

type UtilsOptions = {
  DidKeyMethod: DidKeyMethod;
  DidDhtMethod: DidDhtMethod;
  DidIonMethod: DidIonMethod;
  Jose: Jose;
  Jws: Jws;
}

export class IDDwn {
  agent: IDAgent;
  private connectedDid: string;
  did: DidApi;
  dwn: DwnApi;
  metadata: Metadata;
  jose: Jose;
  protocol: any;
  queue: Queue;
  services: Services;
  transactions: Transactions;
  utils: UtilsOptions;
  vc: VcApi;

  constructor(options: IDOptions) {
    const { agent, connectedDid } = options;
    this.agent = agent;
    this.connectedDid = connectedDid;
    this.did = new DidApi({ agent, connectedDid });
    this.dwn = new DwnApi({ agent, connectedDid });
    this.metadata = new Metadata({ agent, connectedDid });
    this.protocol = Sdk;
    this.queue = new Queue({ agent, connectedDid });
    this.services = new Services({ agent, connectedDid });
    this.transactions = new Transactions({ agent, connectedDid });
    this.utils = { DidKeyMethod, DidDhtMethod, DidIonMethod, Jose, Jws } as UtilsOptions;
    this.vc = new VcApi({ agent, connectedDid, dwnApi: this.dwn });
  }

  /**
   * Connects to a {@link IDAgent}. Defaults to creating a local {@link IDUserAgent}
   * if one isn't provided.
   *
   * @param options - optional overrides
   * @returns
   */
  static async connect(options: IDConnectOptions = {}) {
    let { agent, appData, connectedDid, sync, serviceOptions, passphrase, didMethod, queueWhenOffline, flushWhenOnline } = options;

    // Default to 'ion' for backward compatibility
    didMethod = didMethod ?? 'ion';

    // Apply did:dht relay target globally.
    // Priority:
    // 1) Explicit serviceOptions.dhtRelayUrl
    // 2) Derive from first configured DWN endpoint as `${endpoint}/dht`
    if (serviceOptions?.dhtRelayUrl) {
      DidDht.setRelayUrl(serviceOptions.dhtRelayUrl.replace(/\/+$/, ''));
    } else if (serviceOptions?.dwnEndpoints?.length) {
      const derivedRelayUrl = `${serviceOptions.dwnEndpoints[0]!.replace(/\/+$/, '')}/dht`;
      DidDht.setRelayUrl(derivedRelayUrl);
    }

    if (agent === undefined) {

      // Create the agent (with outbox when queueWhenOffline is not disabled).
      const userAgent: IDManagedAgent = await IDUserAgent.create({ appData, queueWhenOffline });
      agent = userAgent;

      if (passphrase === undefined) {
        passphrase = 'insecure-static-phrase';
      }

      // Start the agent.
      await userAgent.start({ passphrase });

      // Connect attempt failed or was rejected so fallback to local user agent.
      // if (IDUserAgent.isConnected() === false) {

      // Query the Agent's DWN tenant for identity records.
      const identities = await userAgent.identityManager.list();
      const storedIdentities = identities.length;

      // If an existing identity is not found, create a new one.
      if (storedIdentities === 0) {
        // Use the specified DWN endpoints or get default relayer nodes.
        const serviceEndpointNodes = serviceOptions?.dwnEndpoints ?? await getServiceDwnEndpoints();
        
        // Generate DID options based on the selected method
        let didOptions;
        switch (didMethod) {
          case 'dht':
            didOptions = await DidDhtMethod.generateDwnOptions({ serviceEndpointNodes });
            break;
          case 'ion':
            didOptions = await DidIonMethod.generateDwnOptions({ serviceEndpointNodes });
            break;
          case 'key':
            // did:key doesn't need service endpoints or key generation options
            didOptions = {};
            break;
          default:
            throw new Error(`Unsupported DID method: ${didMethod}`);
        }

        // Generate a new Identity for the end-user.
        const identity = await userAgent.identityManager.create({
          name      : 'Default',
          didMethod,
          didOptions,
          kms       : 'local'
        });
        /** Import the Identity metadata to the User Agent's tenant so that it can be restored
         * on subsequent launches or page reloads. */
        await userAgent.identityManager.import({ identity, context: userAgent.agentDid });
        // Set the newly created identity as the connected DID.
        // connectedDid = restoreDid? restoreDid : identity.did;
        connectedDid = identity.did;

      } else {
        // An existing identity was found in the User Agent's tenant.
        const [ identity ] = identities;
        // Set the stored identity as the connected DID.
        // connectedDid = restoreDid? restoreDid : identity.did;
        connectedDid = identity.did;
      }

      // }

      // Enable sync, unless disabled.
      if (sync !== 'off') {
        // First, register the user identity for sync.
        await userAgent.syncManager.registerIdentity({ did: connectedDid });

        // Enable sync using the specified interval or default.
        sync ??= '1m';
        userAgent.syncManager.startSync({ interval: ms(sync) })
          .catch(async (error: Error) => {
            console.error(`Sync failed: ${error}`);
          });
      }

    }

    const iddwn = new IDDwn({ agent, connectedDid });

    if (flushWhenOnline && typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        iddwn.flushOutboxAndSync().catch((err: Error) => {
          console.error('flushOutboxAndSync on online:', err);
        });
      });
    }

    return { iddwn, did: connectedDid };
  }

  /**
   * Run outbox drain then one sync cycle (push + pull). Use when back online
   * to flush queued sends and sync immediately without waiting for the next interval.
   */
  async flushOutboxAndSync(): Promise<void> {
    const managed = this.agent as IDManagedAgent;
    if (managed.outbox) await managed.outbox.drain();
    if (managed.syncManager?.runNow) await managed.syncManager.runNow();
  }
}